export type IId = number | string;

export type ICoord = number[];
export type ICoordXY = [number, number];
export type ICoordXYXY = [ICoordXY, ICoordXY];
export type ICoordXYZW = [number, number, number, number];

export interface ISelect {
  id: IId;
  name: string;
}

export interface IDepartments {
  id: string;
  name: string;
  children: IDepartments[];
}

export interface IRole {
  name: string;
  description: string;
  actions: Record<string, unknown>;
}

export interface IPost {
  id: IId;
  name: string;
}

export interface IOffice {
  id: IId;
  name: string;
  address: string;
  location: {
    lat: number;
    lon: number;
  };
  employeesCount: number;
  plans: Array<{
    id: IId;
    name: string;
  }>;
}

export enum UserStatusPriority {
  HIDDEN = 'HIDDEN',
  NORMAL = 'NORMAL',
  CRITICAL = 'CRITICAL',
  FOCUSED = 'FOCUSED'
}

export interface IUserStatus {
  priority: UserStatusPriority;
  ntfy: {
    class: string;
    classFriendly: string;
    time: string;
  };
}

interface IUserMeasurement {
  type: string;
  value: string;
}

export interface ITrackInfo {
  online: boolean;
  mapColor: string;
  lastPosition: {
    type: string;
    officeId: IId;
    mapId: IId;
    areaId?: IId;
    x: number;
    y: number;
    z?: number;
    isDefinedLocal: boolean;
    lat: number;
    lon: number;
    alt?: number;
    isDefinedGlobal: boolean;
    kinematic: number;
    tolerance: number;
    timeStart: string;
    timeEnd: string;
    'receive-backend': string;
  };
  states: IUserStatus[];
  protAssigned: IId[];
  protChecked: IId[];
  measurements: IUserMeasurement[];
  tag?: {
    id: IId;
    uid: IId;
    tagType: string;
    number: string;
    from: string;
    descr: string;
  };
}

export interface IUser {
  id: IId;
  username: string;
  fullName: string;
  name: string;
  lastname: string;
  secondname: string;
  phone: string;
  email: string;
  title: string;
  role: string;
  brigadeId: number | null;
  brigadeName: string;
  mapColor: string;
  immobilizedTimeMinutes: number;
  workshiftDurationHours: number;
  workshiftBreakMinutes: number;
  descr: string;
  lead: IUser | null;
  protectionsList: IId[];
  birthday: string;
  tabularNumber: string;
  photoContent: string;
  photoContentType: string;
  trackInfo?: ITrackInfo;
  charge?: {
    percent: string | null;
    isCharging: boolean;
    time: string;
    volt: any;
  };
  rootDepartmentId?: IId;
  rootDepartmentName?: string;
  departmentId?: IId;
  departmentName?: string;
}

export type PresenceStatus = 'offline' | 'online' | 'on_plan';

export interface IEmployeePresence {
  userId: IId;
  lastName?: string;
  name?: string;
  secondName?: string;
  presenceStatus: PresenceStatus;
  online: boolean;
  lastContact?: string | null;
  lastPositionTime?: string | null;
  lastPositionType?: string | null;
  mapId?: IId | null;
  areaId?: IId | null;
  officeId?: IId | null;
  hasTag: boolean;
  tagNumber?: string | null;
  mapColor?: string | null;
}

export interface IEmployeePresenceList {
  offlineTimeoutSeconds: number;
  positionTimeoutSeconds: number;
  positionTimeoutSource: string;
  data: IEmployeePresence[];
}

export interface IEmployeesList {
  id: IId;
  fullName: string;
  status?: string;
  post?: {
    id: IId;
    name: string;
  };
  department?: {
    id: IId;
    name: string;
    rootId: string;
    rootName: string;
  };
  deviceId?: number | string;
  deviceCharge?: string;
  lastPositionTime?: string | null;
  lastContact?: string | null;
  lastPositionType?: string | null;
  presenceStatus?: PresenceStatus;
  hasTag?: boolean;
  role?: string;
}

export interface IUserEmployees {
  id: IId;
  tenantId?: IId;
  name: string;
  lastName: string;
  secondName: string;
  fullName?: string;
  phone: string;
  email: string;
  birthday: string;
  authorizationData: {
    login: string;
    password: string;
    hasSsoAuth: boolean;
    changePass: boolean;
  };
  workInfo: {
    department: {
      id: IId;
      name: string;
    };
    post: {
      id: IId;
      name: string;
    };
    tabularNumber: string;
    protections: IId[];
  };
  settings: {
    role: string;
    planColor: string;
    immobilizedTimeMinutes: number;
    workshiftDurationHours: number;
    workshiftBreakMinutes: number;
    managedDepartments: any;
    dataAccesses: any;
  };
  trackerInfo?: {
    deviceId: number;
    deviceCharge: string;
  };
  photoContent?: string;
  photoContentType?: string;
  trackInfo?: ITrackInfo;
  charge?: {
    percent: number;
    isCharging: boolean;
    time: string;
    volt: any;
  };
}

export interface IUserMap extends IUser {
  userId: IId;
  rootDepartmentId?: IId;
  rootDepartmentName?: string;
  departmentId?: IId;
  departmentName?: string;
  position: {
    type: string;
    officeId: IId;
    mapId: IId;
    areaId: IId;
    x: number;
    y: number;
    z: number;
    isDefinedLocal: boolean;
    lat: number;
    lon: number;
    isDefinedGlobal: boolean;
    kinematic: number;
    tolerance: number;
    timeStart: string;
    timeEnd: string;
    'receive-backend': any;
  };
}

export interface IUserPosition {
  user: IUser;
  pos: {
    mapId: IId;
  };
}

export interface IShortUser {
  id: string;
  fullName: string;
}

export enum AreaModes {
  Default = 'DEFAULT',
  Edit = 'EDIT',
  Create = 'CREATE'
}

export enum AreaTypes {
  Box = 'Box',
  Circle = 'Circle',
  Polygon = 'Polygon',
  Marker = 'Marker'
}

export enum EditPlanMode {
  Rotate = 'rotate',
  Translate = 'translate',
  Scale = 'scale',
  Lock = 'none'
}

export interface IArea {
  id: IId;
  mapId: IId;
  name: string;
  descr: string;
  dangerClassId: number;
  mapColor: string;
  workStart: string;
  workEnd: string;
  workDurationMinutes: number;
  /** Zone immobilized timeout in seconds; null/undefined = not set */
  immobilizedTimeSeconds?: number | null;
  protectionsList: IId[];
  denyFrom: IId[];
  appliance: IId;
  employees: IUser[];
  employeesList: IId[];
  geomType: AreaTypes;
  geomCoords: ICoord;
  geomGlobalCoords: ICoord;
}

export interface IAreasAppliance {
  systemName: string;
  friendlyName: string;
  description: string;
}

export interface IBeacon {
  macAddress: string;
  name: string;
  hardwareType: string;
  hardwareTypeFriendly: string;
  lat: number;
  lon: number;
  alt: number;
  x: number;
  y: number;
  z: number;
  officeName: string;
  officeId: IId;
  mapId: string;
  mapName: string;
  charge: number;
  queryTime: string;
  isActive: boolean;
  calibratedRssi: number;
  calibratedDistance: number;
  instanceId: number;
  major: number;
  minor: number;
}

export interface IBeaconType {
  systemName: string;
  friendlyName: string;
}

export interface ICamera {
  id: IId;
  customerId: IId;
  name: string;
  focalLength: number;
  rotationAngle: number;
  description: string;
  isNotificationEnabled: boolean;
  createDateTime: string;
  videoStreamUrl: string;
  location: {
    coordinateX: number;
    coordinateY: number;
    coordinateZ: number;
    lat: number;
    lon: number;
    alt: number;
  };
  plan: {
    id: IId;
    name: string;
  };
  place: {
    id: IId;
    name: string;
  };
}

export interface IInfrastructure {
  id: IId;
  macAddress: string;
  name: string;
  hardwareTypeFriendly: string;
  hardwareType: string;
  x: number;
  y: number;
  z: number;
  officeName: string;
  mapName: string;
  charge: number;
  queryTime: string;
  isActive: boolean;
  instanceId: number;
}

export interface IMap {
  contentId: IId;
  id: IId;
  name: string;
  preview: string;
  previewFormat: string;
  bbox: ICoordXYZW;
  gbox: any;
  areas: IArea[];
  beacons: IBeacon[];
  cameras: ICamera[];
  file?: File | null;
}

export interface IMaps {
  id: IId;
  name: string;
}

export interface IReportAreas {
  userId: IId;
  employeeName: string;
  areaId: IId;
  areaName: string;
  enterTime: string;
  leaveTime: string;
  durationMin: number;
}

export type IAlertVariant = 'success' | 'warning' | 'error';

export interface IAlert {
  uid: IId;
  userId: IId;
  variant?: IAlertVariant;
  type: string;
  title: string;
  description: string;
  color: string;
  isConfirm: boolean;
  date: string;
  severity: NotificationSeverity;
  readTime?: string | null;
  postponeTo?: string | null;
  // Навигационные данные для перехода и фокуса карты
  officeId?: IId | null; // место (office/place)
  placeId?: IId | null; // алиас для officeId, если приходит в таком виде
  mapId?: IId | null;
  areaId?: IId | null;
  lat?: number | null;
  lon?: number | null;
  alt?: number | null;
  trackInfo?: ITrackInfo;
}

export interface IWSAlert {
  messageId: string;
  messageTime: string;
  messageSeverity: NotificationSeverity;
  target: string;
  targetChannel: string;
  targetAddresses: string;
  neutralText: string;
  text: string;
  templateData: {
    id: IId;
    name: string;
    templateSeverity: NotificationSeverity;
    confirmationFlags: string;
    target: string;
    targetChannel: string;
    targetAction: {
      vibro: string;
      sound: string;
    };
    employeesList: IId[];
  };
  sentTime: string | null;
  deliveryTime: string | null;
  readTime: string | null;
  postponeTo?: string | null;
  readText: string | null;
  eventId: string;
  eventTime: string;
  class: INotificationClass;
  classFriendly: string;
  userId: IId;
  areaId: IId | null;
  mapId: IId | null;
  officeId: IId | null;
  placeId: IId | null;
  lat: number | null;
  lon: number | null;
  alt: number | null;
  url: string | null;
  targetAction: {
    vibro: string;
    sound: string;
  };
  targetSimple: ITargetSimple;
  templateId: IId;
  templateName: string;
  employeesList: IId[];
  isNeedConfirmation: boolean;
  confirmSent: boolean;
  confirmDelivery: boolean;
  confirmRead: boolean;
  // Дополнительно: сервер может прислать trackInfo в редких случаях — оставляем для совместимости
  trackInfo?: ITrackInfo;
}

export enum ITargetSimple {
  Web = 'Web',
  Mobile = 'Mobile',
  Other = 'Other'
}

export enum NotificationSeverity {
  Ignored = 'Ignored',
  Low = 'Low',
  Med = 'Med',
  High = 'High',
  Critical = 'Critical'
}

export type INotificationClass =
  | 'NotificationSos'
  | 'NotificationEnteringDangerZone'
  | 'NotificationLeaveDangerZone'
  | 'NotificationForgottenProtection'
  | 'NotificationSilhouetteWoProtection'
  | 'NotificationImmobilized'
  | 'NotificationFall'
  | 'NotificationInformational'
  | 'NotificationWorkStart'
  | 'NotificationWorkEnd'
  | 'NotificationEmergency'
  | 'NotificationHealthPulseAbnormal'
  | 'NotificationAreaExceedingTime'
  | 'NotificationAreaOfftime'
  | 'NotificationProtectionProblem'
  | 'NotificationAreaAccessViolation'
  | 'NotificationTrackerCharging'
  | 'NotificationTrackerUncharged'
  | 'NotificationTrackerLowCharge'
  | 'NotificationLostContact'
  | 'NotificationPlacement';

export interface INotification {
  id: IId;
  uid: IId;
  userId: IId;
  text: string;
  class: INotificationClass;
  classFriendly: string;
  target: string;
  isNeedConfirmation: boolean;
  raisingTime: string;
  targetSimple: ITargetSimple;
  severity: NotificationSeverity;
  url: string;
  trackInfo?: ITrackInfo;
}

export interface IEvent {
  eventUid: string;
  eventTime: string;
  class: INotificationClass;
  classFriendly: string;
  platformText: string;
  supportTargets: string;
  severity: NotificationSeverity;
  areaId: string;
  mapId: string;
  lat: number;
  lon: number;
  alt: number;
  properties: {
    additionalProp1: string;
    additionalProp2: string;
    additionalProp3: string;
  };
  messages: [
    {
      messageId: string;
      messageTime: string;
      messageSeverity: NotificationSeverity;
      targetAddresses: string;
      neutralText: string;
      templateData: {
        id: IId;
        name: string;
        templateSeverity: NotificationSeverity;
        confirmationFlags: string;
        target: string;
        targetChannel: string;
        targetAction: {
          vibro: string;
          sound: string;
        };
        employeesList: IId[];
      };
      sentTime: string;
      deliveryTime: string;
      readTime: string;
      targetChannel: string;
    }
  ];
  confirmSent: string;
  confirmDelivery: string;
  confirmRead: string;
  userId: IId;
  employeesList: IId[];
  previewUrl: string;
}

export interface INotificationChannel {
  channelType: string;
  friendlyName: string;
  description: string;
  channelSpecs: [
    {
      channelCap: string;
      description: string;
    }
  ];
}

export interface INotificationTarget {
  target: string;
  targetSimple: ITargetSimple;
  descr: string;
  channels: INotificationChannel[];
  isList: boolean;
  isReadConfirm: boolean;
  isSound: boolean;
  isVibro: boolean;
}

export interface INotificationType {
  descr: string;
  class: string;
  vars: string;
  targets: INotificationTarget[];
}

export interface INotificationVariable {
  placeholder: string;
  descr: string;
}

export interface INotificationSetting {
  id: IId;
  color: string;
  notificationClass: INotificationClass;
  notificationClassName: string;
  name: string;
  target: string;
  targetName: string;
  template: string;
  employeesList: IId[];
  isNeedConfirmation: boolean;
  isNeedSound: boolean;
  isNeedVibro: boolean;
  targetSimple: ITargetSimple;
}

export interface IProtection {
  id: IId;
  classId: IId;
  className: string;
  numberType: string;
  number: string;
  mappingType: string;
  userId: IId;
  fio: string;
  bestBefore: string;
}

export interface IDevice {
  id: IId;
  number: string;
  className: string;
  mappingType: string;
  fio: string;
  bestBefore: string;
  model: string;
  invent: string;
  serial: string;
  notes: string;
}

export interface IDepartament {
  id: string;
  name: string;
  parentId: string;
  // leadId: IId;
  // leadFio: string;
  // employeesList: IId[];
  employees: Array<{
    id: string;
    fullName: string;
  }>;
}

export interface IEmployeeShort {
  id: IId;
  fullName: string;
}

export interface IViolationsTypes {
  id: IId;
  name: string;
}

export interface IReportViolations {
  id: IId;
  employeeName: string;
  areaName: string;
  mapName: string;
  officeName: string;
  violation: string;
  cause: string;
  time: string;
}

export interface IReportNotifications {
  id: IId;
  time: string;
  mapName: string;
  officeName: string;
  areaName: string;
  eventType: string;
  eventText: string;
  employeeName: string;
}

export interface IReportWorkingTime {
  id: IId;
  employeeName: string;
  start: string;
  end: string;
  graphData: {
    workStart: number;
    actualStart: number;
    workEnd: number;
    actualEnd: number;
    breaks: [
      {
        start: number; // Время начала перерыва в миллисекундах
        end: number; // Время окончания перерыва в миллисекундах
        reason: string; // Причина перерыва
        tempMinutes: number;
      }
    ];
  };
  duration: number;
  nonworkingTime: string;
  workingTime: string;
}

export interface IReportUseProtection {
  id: IId;
  number: string;
  class: {
    id: IId;
    name: string;
  };
  mappingType: string;
  employeeName: string;
  from: string;
  to: string;
}

export interface IReportStayInAreas {
  id: IId;
  npp: number;
  employeeName: string;
  departmentName: string;
  postName: string;
  mapName: string;
  officeName: string;
  areaName: string;
  restrictions: string;
  enterTime: string;
  leaveTime: string;
  duration: string;
}

export interface IReportPhysicalState {
  id: IId;
  physType: string;
  employeeName: string;
  time: string;
  areaName: string;
  mapName: string;
  officeName: string;
}

export interface IReportPhysicalStateType {
  id: IId;
  name: string;
}

export interface IHeatMapPoint {
  x: number;
  y: number;
  lat: number;
  lon: number;
}

export interface ITracks {
  from: string;
  to: string;
  tracks: [];
}

export interface ITrackPoint {
  userId: IId;
  user: {
    id: IId;
    phone: string;
    email: string;
    title: string;
    username: string;
    role: string;
    name: string;
    lastname: string;
    secondname: string;
    descr: string;
  };
  from: string;
  to: string;
  points: [
    {
      // location: {
      //   lat: number;
      //   lon: number;
      //   alt: number;
      // };
      lat: number;
      lon: number;
      alt: number;
      events: [];
      mapId?: IId;
      time?: string;
    }
  ];
}

export interface IReportHeatMapPoint extends IHeatMapPoint {
  weight: number;
  weightNormalized: number;
}

export interface IProtectionType {
  id: IId;
  name: string;
  className: string;
  multiUse: boolean;
}

export interface IProtectionTypeType {
  systemName: string;
  friendlyName: string;
}

export interface IProtectionTechnology {
  numberType: string;
}

export type AccessDecision = 'Grant' | 'Deny' | 'NotApplied';

export interface ISecurableRight {
  id: IId | null;
  uid: string;
  action: string;
  result: AccessDecision;
  roleId?: IId;
  userId?: IId;
}

export interface IRoleType {
  name: string;
  description: string;
}

export interface ICompanyConfig {
  companyName: string;
  dispatcherPhoneNumber: string;
  tenantId: string;
  allowRemapPermProtections: boolean;
  isWebSsoUsed: boolean;
  isLobbyUsed: boolean;
  isGoodwinBppuUsed: boolean;
  isCamUsed: boolean;
  isTabularNumberUsed: boolean;
  isPulseUsed: boolean;
  isProtectionsUsed: boolean;
  isTitleUsed: boolean;
  isSendDeviceNotificationAvailable: boolean;
  userActivityTrackingSettings: {
    offlineTimeoutSecondsMin: number;
    offlineTimeoutSeconds: number;
    actualPositionTimeoutSecondsMin: number;
    actualPositionTimeoutSeconds: number;
    leaveAreaTimeoutSecondsMin: number;
    leaveAreaTimeoutSeconds: number;
    immobilizedTimeMinutes: number;
    immobilizedAfterFallSeconds: number;
    immobilizedCheckWindowSeconds: number;
    sendSuccessTimeoutSeconds: number;
    sendErrorRepeatTimeoutSeconds: number;
    immobilizedZoneFlags: string;
    immobilizedOrientation: string;
    fallOrientation: string;
    sourceFlags: string;
  };
  workshiftSettings: {
    workshiftDurationHours: number;
    workshiftBreakMinutes: number;
    workshiftCooldownMinutes: number;
    workshiftCheckPeriodMinutes: number;
    immobilizedWorkshift: string;
  };
  mapKeys: {
    tomtom: string;
    arcgis: string;
    mapbox: string;
    urls?: Record<string, string>;
  };
  hazardTimer?: {
    delayTimerSeconds: number[];
  };
  notificationConfirmText?: string[];
  loaded: string;
}
